/**
 * Inventory & Products analytics section.
 *
 * Reuses `GET /inventory/statistics` + `GET /products/stats` (via the home
 * analytics hook) to show stock health and brand/type breakdowns.
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import {Boxes} from 'lucide-react';
import AnalyticsSectionCard from '../AnalyticsSectionCard';
import MetricTile from '../MetricTile';
import MiniBarList from '../MiniBarList';
import ProgressRing from '../ProgressRing';
import {useInventoryProductsAnalytics} from '../../hooks/useHomeAnalytics';

interface InventoryProductsSectionProps {
  enabled: boolean;
  serialsEnabled: boolean;
}

const InventoryProductsSection: React.FC<InventoryProductsSectionProps> = ({
  enabled,
  serialsEnabled
}) => {
  const {t} = useTranslation();
  const {data, isLoading, isError} = useInventoryProductsAnalytics(enabled);

  const stockHealth =
    data && data.totalProducts > 0
      ? (data.inStock / data.totalProducts) * 100
      : 0;

  return (
    <AnalyticsSectionCard
      title={t('analytics.inventory_products.title')}
      icon={<Boxes className="w-5 h-5" />}
      isLoading={isLoading}
      isError={isError}
    >
      {data && (
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <ProgressRing
              percentage={stockHealth}
              tone="success"
              label={t('analytics.inventory_products.stock_health')}
            />
            <div className="grid grid-cols-2 gap-2 flex-1">
              <MetricTile
                label={t('analytics.inventory_products.total_products')}
                value={data.totalProducts}
                tone="primary"
              />
              <MetricTile
                label={t('analytics.inventory_products.in_stock')}
                value={data.inStock}
                tone="success"
              />
              <MetricTile
                label={t('analytics.inventory_products.low_stock')}
                value={data.lowStock}
                tone="warning"
              />
              <MetricTile
                label={t('analytics.inventory_products.out_of_stock')}
                value={data.outOfStock}
                tone="error"
              />
            </div>
          </div>

          {serialsEnabled && (
            <div className="grid grid-cols-2 gap-2">
              <MetricTile
                label={t('analytics.inventory_products.total_serials')}
                value={data.totalSerials ?? 0}
                tone="neutral"
              />
              <MetricTile
                label={t('analytics.inventory_products.available_serials')}
                value={data.availableSerials ?? 0}
                tone="neutral"
              />
            </div>
          )}

          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              {t('analytics.inventory_products.by_brand')}
            </h4>
            <MiniBarList
              rows={data.productsByBrand}
              tone="primary"
              emptyLabel={t('analytics.no_data')}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              {t('analytics.inventory_products.by_type')}
            </h4>
            <MiniBarList
              rows={data.productsByType}
              tone="primary"
              emptyLabel={t('analytics.no_data')}
            />
          </div>
        </div>
      )}
    </AnalyticsSectionCard>
  );
};

export default InventoryProductsSection;
